<script setup>
import {computed, reactive, inject, ref, onMounted, watch, provide} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {discordRegistrationTime} from "@/utils/discordRegistrationTime.js";
import {formatPrice} from "@/utils/priceFormatter.js";
import {cleanObject} from "@/utils/cleanObject.js";
//import components
import {Icon} from "@iconify/vue";
import {
  Dialog,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  InputGroup,
  InputGroupAddon,
  InputText,
  FloatLabel,
  Select,
  Textarea,
  InputNumber,
  Rating,
  SelectButton
} from "primevue";
import Attachments from "@/views/orders/Details/Attachments.vue";
import Users from "@/components/Users.vue";
import UserProfile from "@/components/UserProfile/index.vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useVuelidate} from "@vuelidate/core";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {useRouter} from "vue-router";
import {showMessage} from "@/utils/showMessage.js";

//stores
const userStore = useUserStore();
const currencyPricesStore = useCurrencyPricesStore();
const ordersStore = useMainOrdersStore()

//loading flags
const {
  saving,
  fetching,
  setSaving,
  setFetching,
} = useLoadingFlags()
const isUploadingAttachment = ref(false)
const isShowUserProfileModal = ref(false);

//other composables
const {isXL} = useBreakpoint();
const toast = useToast();
const router = useRouter()

//emits
const emit = defineEmits(['save', 'refreshHotOrders']);

//props
const props = defineProps({
  orderType: {
    type: String,
    required: true
  },
  hotorder: {
    type: Object,
    required: true
  },
  onlyRating: {
    type: Boolean,
    default: false
  }
})

//injects
const visible = inject('isShowCanceledRatingModal')

//component variables
const rating = ref(null)
const tags = ref([])
const form = reactive({
  "tags": props.hotorder.tags ? props.hotorder.tags : [],
  "rating": 1,
  "review": props.hotorder.review ? props.hotorder.review : null
})
const rules = computed(() => {
  return {
    rating: {required},
    review: {required}
  };
});
const $vuelidate = useVuelidate(rules, form);


//component functions
const getTags = async () => {
  try {
    setFetching(true)
    const res = await ordersStore.action_getTags()
    if (res.result === true) {
      tags.value = res.data
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    setFetching(false)
  }
}

const saveRating = async () => {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }

  setSaving(true)
  const orderType = {
    Item: "Item",
    CurrencyBuy: "Currency",
    Boost: "Boost",
    'Gift Card': "Gift Card",
    Account: "Account",
  }
  const cleanRequest = {
    "id": props.hotorder.id,
    "type": orderType[props.orderType],
    "tags": form.tags,
    "rating": form.rating,
    "review": form.review,
    "userID": props.hotorder.takerID
  }
  const res = await ordersStore.action_setRatingAndTags(cleanRequest)
  if (res.result === true) {
    //toast.add({severity: 'success', summary: 'Successful', detail: `Rates and Tags applied!`, life: 1500});
    showMessage(`Rates and Tags applied!`, 'success');
    if (props.onlyRating) {
      emit('refreshHotOrders');
      //console.log('only rating')
    } else {
      emit('save');
    }
    visible.value = false
  } else {
    //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
    showMessage(res, 'error');
  }
  setSaving(false)
}

//hooks
onMounted(() => {
  getTags()
})

/*
watch(() => form.rating, (newVal, oldVal) => {
  console.log(newVal, oldVal)
})
*/

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        Negative Rating
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div v-if="onlyRating" class="bg-slate-800 rounded-md p-3 border border-slate-700">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs max-w-2xl">
          <div>
            <p class="text-slate-400">Title</p>
            <p class="text-white w-full text-ellipsis whitespace-nowrap overflow-hidden">{{ hotorder.title }}</p>
          </div>
          <div>
            <p class="text-slate-400">Taker</p>
            <p class="text-white w-full text-ellipsis whitespace-nowrap overflow-hidden">{{ hotorder.takerName }}</p>
          </div>
          <div>
            <p class="text-slate-400">Rating Admin Name</p>
            <p class="text-white w-full text-ellipsis whitespace-nowrap overflow-hidden">{{ hotorder.ratingAdminName || '-' }}</p>
          </div>
        </div>
      </div>
      <div class="flex flex-col items-center gap-4">
        <Rating :stars="1" :defaultValue="1">
          <template #onicon>
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 128 128">
              <path fill="#fac036"
                    d="M79.46 73.8c6.15 1.74 17.05 1.51 20.24 1.28c5.58-.4 11.16-.28 12.12-7.05c.64-4.56-.89-8.71-7.65-9.45c-2.38-.26-5.29-.4-7.69-.78l-1.88-2.07c2.22.3 7.31.2 8.84.39c6.46.79 11.92-1.8 12.74-8.01c.72-5.44-3.38-9.64-9.2-9.32c-1.99.11-7.61-.66-13.19-.81l-.43-1.13l-.2-.34c3.54-.1 6.93-.24 9.34-.36c6.45-.32 9.97-3.78 9.88-8.38c-.11-5.59-5.34-7.69-9.08-8c-2.8-.23-12.82.06-19.55.61l-1.06-1.81c.48-.01 13.37-.63 13.86-.66c5.91-.44 8.43-2.58 8.28-6.8c-.21-5.92-6.53-7.49-9.95-8.03c-5.55-.88-12.95-.62-18.34-.77c-11.66-.32-35.52-.84-46.66 8.92c-3.8 3.33-7.63 7.86-10.89 9.16c-6.83 2.72-7.42 11.62-7.11 22.87c.15 5.44-1.72 20.71 4.88 24.41c9.26 5.19 24.96 9.11 30.18 13.21c8.56 6.73 13.56 17.68 15.84 24.09C67.12 117.18 68.44 126 76.03 126c13.3 0 12.95-11.88 13.2-17.82c.49-11.63-13.86-33.15-9.77-34.38"/>
              <path fill="#e48c15"
                    d="M68.02 47.06c.13-5.99 4.75-8.56 4.96-8.67c-2.65.06-8.39-2.63-8.39-8.34c0-4.71 2.56-8.38 5.13-9.63c-1.69-.36-4.88-2.66-4.88-7.68c0-7.36 5.46-10.05 9.59-10.56s15.61.18 19.4.76c2.03.31-10.06.45-18.91 1.61c-2.54.33-5.57 2.72-6.06 5.94c0 0-.72 4.25 3.16 6.7c0 0 1.64 1.41 9.25 1.35l2.3-.03c.72-.05 9.91-.37 12.97-.59c1.35-.1 2.51-.29 3.51-.57c-.16.23-.34.61-.28 1.16c.13 1.16.89 1.21 1.09 1.21l-1.82.1l-6.48.37c-6.46.39-15.56 1.01-16.61 1.43c0 0-6.34 1.6-6.34 8.1c0 0-.33 5.53 8.13 5.79c0 0 19.99 1.15 28.45.03c0 0-1.21 2.25.79 3.26c0 0-14.27-.35-23.21-.59s-10.85 4.51-11.08 7.1c-.03.39-.11 1.01-.11 1.74c0 5.15 4.52 5.83 10.99 6.67c13.96 1.83 19.87 2.4 19.87 2.4c.77.09 1.5.14 2.21.13c-.39.22-.92.63-.99 1.31c-.04.44.18 1.11.18 1.11s-6.02-.49-15.31-1.73c-9.28-1.24-16.7-.67-17.37 4.76c-.49 3.98.18 6.51 1.9 8.48c2.12 2.43 5.53 3.64 5.53 3.64s-.39.08-.61.37c-.22.3-.23.77-.23.77C71.18 74 66 69.19 66.43 63.01c.3-4.23 1.42-7.05 4.23-8.42c.01.01-2.77-1.55-2.64-7.53"/>
              <path fill="#e48c15"
                    d="m71.11 60.48l-3.32-.96s4.92-18.11-13.24-30.59c0 0-2.33-1.56-1.2-2.73c0 0 .79-1.36 4.13 1.19c0 .01 17.72 11.57 13.63 33.09"/>
            </svg>
          </template>
          <template #officon>
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 128 128">
              <path fill="#fadcbc"
                    d="M79.46 73.8c6.15 1.74 17.05 1.51 20.24 1.28c5.58-.4 11.16-.28 12.12-7.05c.64-4.56-.89-8.71-7.65-9.45c-2.38-.26-5.29-.4-7.69-.78l-1.88-2.07c2.22.3 7.31.2 8.84.39c6.46.79 11.92-1.8 12.74-8.01c.72-5.44-3.38-9.64-9.2-9.32c-1.99.11-7.61-.66-13.19-.81l-.43-1.13l-.2-.34c3.54-.1 6.93-.24 9.34-.36c6.45-.32 9.97-3.78 9.88-8.38c-.11-5.59-5.34-7.69-9.08-8c-2.8-.23-12.82.06-19.55.61l-1.06-1.81c.48-.01 13.37-.63 13.86-.66c5.91-.44 8.43-2.58 8.28-6.8c-.21-5.92-6.53-7.49-9.95-8.03c-5.55-.88-12.95-.62-18.34-.77c-11.66-.32-35.52-.84-46.66 8.92c-3.8 3.33-7.63 7.86-10.89 9.16c-6.83 2.72-7.42 11.62-7.11 22.87c.15 5.44-1.72 20.71 4.88 24.41c9.26 5.19 24.96 9.11 30.18 13.21c8.56 6.73 13.56 17.68 15.84 24.09C67.12 117.18 68.44 126 76.03 126c13.3 0 12.95-11.88 13.2-17.82c.49-11.63-13.86-33.15-9.77-34.38"/>
              <path fill="#dba689"
                    d="M68.02 47.06c.13-5.99 4.75-8.56 4.96-8.67c-2.65.06-8.39-2.63-8.39-8.34c0-4.71 2.56-8.38 5.13-9.63c-1.69-.36-4.88-2.66-4.88-7.68c0-7.36 5.46-10.05 9.59-10.56s15.61.18 19.4.76c2.03.31-10.06.45-18.91 1.61c-2.54.33-5.57 2.72-6.06 5.94c0 0-.72 4.25 3.16 6.7c0 0 1.64 1.41 9.25 1.35l2.3-.03c.72-.05 9.91-.37 12.97-.59c1.35-.1 2.51-.29 3.51-.57c-.16.23-.34.61-.28 1.16c.13 1.16.89 1.21 1.09 1.21l-1.82.1l-6.48.37c-6.46.39-15.56 1.01-16.61 1.43c0 0-6.34 1.6-6.34 8.1c0 0-.33 5.53 8.13 5.79c0 0 19.99 1.15 28.45.03c0 0-1.21 2.25.79 3.26c0 0-14.27-.35-23.21-.59s-10.85 4.51-11.08 7.1c-.03.39-.11 1.01-.11 1.74c0 5.15 4.52 5.83 10.99 6.67c13.96 1.83 19.87 2.4 19.87 2.4c.77.09 1.5.14 2.21.13c-.39.22-.92.63-.99 1.31c-.04.44.18 1.11.18 1.11s-6.02-.49-15.31-1.73c-9.28-1.24-16.7-.67-17.37 4.76c-.49 3.98.18 6.51 1.9 8.48c2.12 2.43 5.53 3.64 5.53 3.64s-.39.08-.61.37c-.22.3-.23.77-.23.77C71.18 74 66 69.19 66.43 63.01c.3-4.23 1.42-7.05 4.23-8.42c.01.01-2.77-1.55-2.64-7.53"/>
              <path fill="#dba689"
                    d="m71.11 60.48l-3.32-.96s4.92-18.11-13.24-30.59c0 0-2.33-1.56-1.2-2.73c0 0 .79-1.36 4.13 1.19c0 .01 17.72 11.57 13.63 33.09"/>
            </svg>
          </template>
        </Rating>
        <!--        <template v-if="$vuelidate.rating.$error">
                  <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                     v-if="!$vuelidate.rating.required.$response">
                    You must rate this taker before continuing.
                  </p>
                </template>-->
      </div>
      <div class="grid grid-cols-1 gap-6">
        <div class="flex flex-col gap-2">
          <p class="text-center select-none">Negative Points</p>
          <div v-if="fetching" class="grid grid-cols-1 md:grid-cols-2 gap-2 animate-pulse">
            <div v-for="i in 6" class="w-40 h-9 bg-slate-800 rounded-md"></div>
          </div>
          <SelectButton v-else v-model="form.tags" :options="tags.filter(t => t.sign === '-')" optionLabel="tag"
                        optionValue="tag" multiple
                        aria-labelledby="multiple" class="!grid !grid-cols-1 md:!grid-cols-2 !gap-2 negative"/>
        </div>
        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on" :class="{ 'has-error': $vuelidate.review.$error }">
            <Textarea v-auto-dir id="review" v-model="form.review" rows="5" style="resize: none"
                      :class="['!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700',
                           { 'has-error': $vuelidate.review.$error }
                         ]"/>
            <label for="review" class="!bg-slate-900">Review</label>
          </FloatLabel>
          <template v-if="$vuelidate.review.$error">
            <p class="text-red-300 text-[.6rem] 2xl:text-xs"
               v-if="!$vuelidate.review.required.$response">
              This field is required
            </p>
          </template>
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-3">
      <button v-if="!onlyRating" @click="() => {emit('save')}"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1">
        Skip Rating
      </button>
      <button @click="saveRating"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              :disabled="saving">
        Save
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
      </button>
    </div>
  </Dialog>
</template>
<style scoped>
:deep(.p-rating-option .p-rating-icon) {
  --p-rating-icon-size: 1.5rem
}
</style>